// covered-by: packages/schemas/src/christo/__tests__/askPunttHomeCopy.test.ts
/**
 * AskPuntt V2 home copy contract.
 *
 * The home page renders its default (translated) headline, subheading, and
 * summary immediately, then asks christo for a personalized version written
 * by one LLM call over the user, their enterprise, and their local time.
 * `copy` is null whenever personalization did not happen (producer down,
 * missing config, model error) — the FE keeps the defaults in that case.
 */
import { z } from "zod";

export const ASK_PUNTT_HOME_COPY_LIMITS = {
  headline: 60,
  subheading: 90,
  summary: 320,
} as const;

export const askPunttHomeCopySchema = z.object({
  headline: z.string().min(1).max(ASK_PUNTT_HOME_COPY_LIMITS.headline),
  subheading: z.string().min(1).max(ASK_PUNTT_HOME_COPY_LIMITS.subheading),
  summary: z.string().min(1).max(ASK_PUNTT_HOME_COPY_LIMITS.summary),
});
export type AskPunttHomeCopy = z.infer<typeof askPunttHomeCopySchema>;

/**
 * Client clock, sent verbatim so the server can describe the user's day
 * without guessing their zone. `localDateTime` is the wall-clock time in
 * `timezone`, ISO-shaped and second-precision optional: `2026-09-16T22:06`.
 */
export const askPunttHomeCopyClientTimeSchema = z.object({
  timezone: z.string().min(1).max(64),
  localDateTime: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/,
      "localDateTime must look like YYYY-MM-DDTHH:mm",
    ),
  locale: z.string().min(2).max(16).optional(),
});
export type AskPunttHomeCopyClientTime = z.infer<
  typeof askPunttHomeCopyClientTimeSchema
>;

export const getAskPunttHomeCopyRequestSchema = z.object({
  body: askPunttHomeCopyClientTimeSchema,
});

export const getAskPunttHomeCopyResponseSchema = z.object({
  copy: askPunttHomeCopySchema.nullable(),
  personalized: z.boolean(),
  generatedAt: z.string(),
});
export type GetAskPunttHomeCopyResponse = z.infer<
  typeof getAskPunttHomeCopyResponseSchema
>;
